import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import useDC from '@eneco/dc/useDC';
import { FlowError, StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Box, Card, Divider, Stack, Text, TextLink, ToggleSwitch } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SustainableChoiceStepRendering } from '@sitecore/types/SustainableChoiceStep';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { getOffer, getUpsellProductPrice, energyTypeIncludes, getOfferAlternative } from '../../../shared/helpers';
import { useFlowTracking, useFlowNavigation, useCachedOfferMachine } from '../../../shared/hooks';
import { EnergyType } from '../../../shared/types';

interface FormFields {
  hasUpsellGas?: boolean;
  hasUpsellElectricity?: boolean;
}

export const SustainableChoiceStep: FC = () => {
  const { businessUnit, label } = useDC();
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: flowContext,
    context: {
      energyType = EnergyType.Electricity,
      hasUpsellGas,
      hasUpsellElectricity,
      isGasUpsellProductAvailable,
      isElectricityUpsellProductAvailable,
      isSME,
    },
  } = flowState;

  const { offerState, sendToOfferMachine } = useCachedOfferMachine(businessUnit, label);

  const offer = offerState?.context?.offer;

  const { SustainableChoiceStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SustainableChoiceStep: SustainableChoiceStepRendering;
  }>();

  const formSchema = yup.object({
    hasUpsellGas: yup.boolean(),
    hasUpsellElectricity: yup.boolean(),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasUpsellGas: !!hasUpsellGas,
      hasUpsellElectricity: !!hasUpsellElectricity,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const mutatedHasUpsellGas = watch('hasUpsellGas');
  const mutatedHasUpsellElectricity = watch('hasUpsellElectricity');

  const { trackAddToCart, trackRemoveFromCart } = useFlowTracking();

  const selectedOffer = getOffer(flowContext, offer);
  const availableOffer = getOfferAlternative(
    {
      ...flowContext,
      hasUpsellGas: mutatedHasUpsellGas,
      hasUpsellElectricity: mutatedHasUpsellElectricity,
    },
    offer,
  );

  const priceUpsellGas = energyTypeIncludes(energyType, EnergyType.Gas)
    ? getUpsellProductPrice('gas', flowContext, offer)
    : 0;
  const priceUpsellElectricity = energyTypeIncludes(energyType, EnergyType.Electricity)
    ? getUpsellProductPrice('electricity', flowContext, offer)
    : 0;

  const { currency } = useFormatter();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  useEffect(() => {
    sendToOfferMachine({
      type: flowContext?.isContractExtension ? 'GET_EXTENSION_OFFER' : 'GET_OFFER',
      flowContext: { ...flowContext },
    });
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formValues = getValues();

      if (
        (formValues.hasUpsellGas !== hasUpsellGas || formValues.hasUpsellElectricity !== hasUpsellElectricity) &&
        selectedOffer &&
        availableOffer
      ) {
        trackRemoveFromCart(selectedOffer, offer);
        trackAddToCart(availableOffer, offer);
      }

      updateContextAndGoToNextStep({
        ...formValues,
        ...((formValues.hasUpsellGas !== hasUpsellGas || formValues.hasUpsellElectricity !== hasUpsellElectricity) && {
          discountCode: availableOffer?.type,
        }),
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING') || offerState.matches('GET_EXTENSION'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
    }
  }, [offerState]);

  const getErrorNotification = () => {
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;

    return textualData?.genericError?.errorNotification;
  };

  return (
    <Layout.Content variant="A" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} />
          <Stack.Item>
            {isElectricityUpsellProductAvailable && (
              <Card>
                <Box padding="6">
                  <Stack direction="column" gap="1">
                    <Text id="upsell-electricity-heading">
                      {textualData?.upsellDetails?.electricityProductName?.value}
                    </Text>

                    <Stack direction="row" gap="1" alignY="center" alignX="justify">
                      <Text size="BodyXL" weight="bold">
                        {currency.euro(priceUpsellElectricity ?? 0)}{' '}
                        <Text size="BodyM" weight="regular" color="textLowEmphasis">
                          {textualData?.upsellDetails?.byPeriodLabel?.value}
                        </Text>
                      </Text>

                      <Controller
                        control={control}
                        name="hasUpsellElectricity"
                        render={({ field: { onChange, value, name, ref } }) => (
                          <ToggleSwitch
                            aria-labelledby="upsell-electricity-heading"
                            onCheckedChange={onChange}
                            isChecked={value}
                            name={name}
                            ref={ref}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </Card>
            )}

            {isGasUpsellProductAvailable && (
              <Box paddingTop="1">
                <Card>
                  <Box padding="6">
                    <Stack direction="column" gap="1">
                      <Text id="upsell-gas-heading">{textualData?.upsellDetails?.gasProductName?.value}</Text>

                      <Stack direction="row" gap="1" alignY="center" alignX="justify">
                        <Text size="BodyXL" weight="bold">
                          {currency.euro(priceUpsellGas ?? 0)}{' '}
                          <Text size="BodyM" weight="regular" color="textLowEmphasis">
                            {textualData?.upsellDetails?.byPeriodLabel?.value}
                          </Text>
                        </Text>

                        <Controller
                          control={control}
                          name="hasUpsellGas"
                          render={({ field: { onChange, value, name, ref } }) => (
                            <ToggleSwitch
                              aria-labelledby="upsell-gas-heading"
                              onCheckedChange={onChange}
                              isChecked={value}
                              name={name}
                              ref={ref}
                            />
                          )}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                </Card>
              </Box>
            )}
          </Stack.Item>

          <Card>
            <Text size="BodyS">
              <Box padding="3">
                <Stack direction="column" gap="6">
                  <Stack direction="row" gap="1" alignY="center" alignX="justify">
                    <Text>{textualData?.upsellDetails?.costsWithoutUpsellTitle?.value}</Text>
                    <Text>{currency.euro(selectedOffer?.costsMonthly?.[costsType] ?? 0)}</Text>
                  </Stack>

                  {isElectricityUpsellProductAvailable && (
                    <Stack.Item>
                      <Text color={mutatedHasUpsellElectricity ? undefined : 'textLowEmphasis'}>
                        <Stack direction="row" gap="1" alignY="center" alignX="justify">
                          <Text>
                            {'+ '}
                            {textualData?.upsellDetails?.electricityProductName?.value}
                          </Text>
                          <Text>{currency.euro(mutatedHasUpsellElectricity ? priceUpsellElectricity : 0)}</Text>
                        </Stack>
                      </Text>
                    </Stack.Item>
                  )}

                  {isGasUpsellProductAvailable && (
                    <Stack.Item>
                      <Text color={mutatedHasUpsellGas ? undefined : 'textLowEmphasis'}>
                        <Stack direction="row" gap="1" alignY="center" alignX="justify">
                          <Text>
                            {'+ '}
                            {textualData?.upsellDetails?.gasProductName?.value}
                          </Text>
                          <Text>{currency.euro(mutatedHasUpsellGas ? priceUpsellGas : 0)}</Text>
                        </Stack>
                      </Text>
                    </Stack.Item>
                  )}
                </Stack>
              </Box>

              <Stack.Item>
                <Divider />
                <Box padding="3">
                  <Stack direction="row" gap="1" alignY="center" alignX="justify">
                    <Text>{textualData?.upsellDetails?.costsWithUpsellTitle?.value}</Text>
                    <Text weight="bold">{currency.euro(availableOffer?.costsMonthly?.[costsType] ?? 0)}</Text>
                  </Stack>
                </Box>
              </Stack.Item>
            </Text>
          </Card>

          {textualData?.content?.moreInfoDialog?.value?.triggerText && (
            <TrackedDialog
              title={textualData?.content?.moreInfoDialog?.value?.title}
              trigger={<TextLink emphasis="high">{textualData?.content?.moreInfoDialog?.value?.triggerText}</TextLink>}>
              <RichText html={textualData?.content?.moreInfoDialog?.value?.content} />
            </TrackedDialog>
          )}

          <Text size="BodyS" color="textLowEmphasis" weight="regular">
            {textualData?.content?.footnoteText?.value && <RichText html={textualData?.content?.footnoteText?.value} />}
          </Text>

          <Box paddingTop={3}>
            <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
          </Box>
        </>
      )}

      {isFailed && (
        <FlowError
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: !isNotAvailable && !isNotFound ? textualData?.genericError?.tryAgainButtonText : undefined,
          }}
        />
      )}
    </Layout.Content>
  );
};
